import { BORDER_STYLE, OUTLINE_STYLE, MAIN_STYLE_GROUPS, PADDING_AXES, TOGGLE_EVENTS } from '../shared.js'
export const checkbox = {

    displayName: 'Checkbox',

    // LVGL-style parts — label/exportSuffix/order are the source of truth for
    // the Property Editor and for legacy JSONL wire keys (see v2/shared.js
    // part metadata doc and v2/wireFormat.js toWireKey).
    //
    // 'bullet' is openHASP's name for what LVGL 9 calls the checkbox's
    // INDICATOR part (the little square/checkmark box) — every theme file
    // already uses the indicator wire suffix (bg_color10/border_color10/
    // border_width10) for it on export. Before this fix, neither this schema
    // nor the legacy OPENHASP_PART_SUFFIX fallback map had an entry for the
    // custom name 'bullet', so its wire keys silently computed as bare
    // (unsuffixed) — identical to main's — which meant HaspCheckbox.js's own
    // reads of the 'bullet' part resolved main's values instead. See
    // HaspCheckbox.js for the resulting widget-side fix.
    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                // bg_color:         { type: 'color', format: 'hex8' },
                bg_opa:           { type: 'int', min: 0, max: 255, internal: true },
                ...BORDER_STYLE,
                ...OUTLINE_STYLE,
                radius:           { type: 'int', min: 0, max: 999 },
                text_color:       { type: 'color' },
                text_font:        { type: 'int', min: 8 },
                text_font_family: { type: 'string', internal: true },
            },
        },
        bullet: {
            label: 'Bullet',
            exportSuffix: '10',
            order: 1,
            properties: {
                bg_color:     { type: 'color' },
                border_width: { type: 'int', min: 0 },
                border_color: { type: 'color' },
                radius:       { type: 'int', min: 0, max: 999 },
                // lv_theme_hasp.c's cb_bullet style sets pad_all(HASP_DPX(3)),
                // a real padding that directly determines the bullet box's
                // on-screen size independent of font size (see HaspCheckbox.js).
                //
                // The bullet only distinguishes HORIZONTAL from VERTICAL
                // padding — its box grows symmetrically around the label's line
                // height on each axis, so top/bottom (and left/right) are NOT
                // separately meaningful and this part deliberately declares
                // PADDING_AXES rather than the four PADDING_SIDES other parts
                // use. pad_hor/pad_ver each expand to their axis' PAIR of
                // bullet wire keys (pad_left10+pad_right10 / pad_top10+
                // pad_bottom10), and the `padding` shorthand still fills all
                // four — both handled by ObjectExport.js's padding-expansion
                // special case and folded back by ConfigManager.js on import.
                padding: { type: 'int', min: 0, exportSpecial: true },
                ...PADDING_AXES,
            },
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        // width: { type: 'int', min: 0 }, height: { type: 'int', min: 0 },
        // min_width: { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        enabled:  { type: 'bool' },
        text:     { type: 'string' },
        val:      { type: 'int' },
        groupid:  { type: 'int', min: 0, max: 15, exportSpecial: true },
        entityid: { type: 'string', internal: true },
        // Font Family selection — see label.js's `font` property comment for
        // the full wire-format semantics.
        font: { type: 'resource', resourceType: 'font', label: 'Font Family', inline: true },
    },

    defaults: {
        properties: {
            x: 0, y: 0,
            // width: 100, height: 30, min_width: 20, min_height: 10,
            enabled: true,
            text: 'Check box', val: 0,
            font: '',
        },
        styles: {
            main: { border_width: 0 },
            // radius: 4 and padding: 3 match firmware's cb_bullet theme style
            // (lv_theme_hasp.c) — rounds the bullet square's corners and
            // sizes it independent of font size.
            bullet: { radius: 4, padding: 3 },
        },
    },
    states: ['checked', 'pressed', 'pressedChecked', 'disabled', 'disabledChecked'],
    // can_transform: shows the Transformer's selection border for visual
    // feedback when selected — HaspCheckbox.js's getResizeMode() override
    // returns 'none' so no resize handles appear (checkbox has no width/
    // height to resize; it always auto-fits to its content).
    editorCapabilities:  ['can_transform', 'can_copy', 'has_objectmenu'],
    runtimeCapabilities: ['has_action', 'has_group'],

    events: TOGGLE_EVENTS,

    convertTo: ['switch'],

    bindings: {
        properties: { val: {}, text: {} },
        actions: { hass_services: ['', 'homeassistant', 'openhasp', 'automation', 'climate', 'light', 'switch', 'esphome'] },
    },

    automation: {
        runtimeVariables: [
            { name: 'val', type: 'int', label: 'Checked' },
        ],
        actionDomains: ['switch', 'light', 'homeassistant', 'openhasp', 'esphome'],
    },
}
